import DOMPurify from 'dompurify';

// Lightweight, dependency-free Markdown → sanitized HTML for chat assistants.
// Handles headers, bold/italic/code, links, bullet & numbered lists, blockquote
// callouts, horizontal rules, and GitHub-style tables. Output is Tailwind-styled
// and DOMPurify-sanitized, so it's safe to drop into dangerouslySetInnerHTML.

const escapeHtml = (s) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Inline formatting applied to already-escaped text.
const inline = (s) => {
  let t = escapeHtml(s);
  t = t.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-secondary text-[0.85em]">$1</code>');
  t = t.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>');
  t = t.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary underline">$1</a>');
  return t;
};

const splitRow = (line) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

const renderTable = (header, rows) => {
  const th = header.map((h) => `<th class="text-left font-semibold text-foreground px-2.5 py-1.5 border-b border-border">${inline(h)}</th>`).join('');
  const trs = rows.map((r) =>
    `<tr>${r.map((c) => `<td class="px-2.5 py-1.5 border-b border-border/50 align-top">${inline(c)}</td>`).join('')}</tr>`
  ).join('');
  return `<div class="overflow-x-auto my-2.5"><table class="w-full text-[12.5px] border border-border rounded-lg border-separate border-spacing-0"><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></div>`;
};

const isBlock = (l) =>
  /^\s*([-*]\s+|\d+\.\s+|#{1,4}\s+|>\s?|\|)/.test(l) || /^\s*(---|\*\*\*|___)\s*$/.test(l);

export function mdToHtml(md) {
  const lines = (md || '').replace(/\r/g, '').split('\n');
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table: header row + separator row (|---|---|)
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length &&
        /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { rows.push(splitRow(lines[i])); i++; }
      out.push(renderTable(header, rows));
      continue;
    }

    // Headers
    let m;
    if ((m = line.match(/^####\s+(.*)/))) { out.push(`<p class="text-[12px] font-semibold text-foreground mt-3 mb-1 uppercase tracking-wide">${inline(m[1])}</p>`); i++; continue; }
    if ((m = line.match(/^###\s+(.*)/))) { out.push(`<h5 class="text-[13px] font-semibold text-foreground mt-3 mb-1">${inline(m[1])}</h5>`); i++; continue; }
    if ((m = line.match(/^##\s+(.*)/))) { out.push(`<h4 class="text-sm font-semibold text-foreground mt-3.5 mb-1.5">${inline(m[1])}</h4>`); i++; continue; }
    if ((m = line.match(/^#\s+(.*)/))) { out.push(`<h3 class="text-[15px] font-semibold text-foreground mt-3.5 mb-1.5">${inline(m[1])}</h3>`); i++; continue; }

    // Horizontal rule
    if (/^\s*(---|\*\*\*|___)\s*$/.test(line)) { out.push('<hr class="my-3 border-border" />'); i++; continue; }

    // Blockquote → callout box
    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      out.push(`<div class="my-2 border-l-2 border-primary/50 bg-primary/5 rounded-r-lg px-3 py-2 text-foreground/90">${inline(quote.join(' '))}</div>`);
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(inline(lines[i].replace(/^\s*[-*]\s+/, ''))); i++; }
      out.push('<ul class="list-disc pl-5 space-y-1 my-2">' + items.map((it) => `<li>${it}</li>`).join('') + '</ul>');
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(inline(lines[i].replace(/^\s*\d+\.\s+/, ''))); i++; }
      out.push('<ol class="list-decimal pl-5 space-y-1 my-2">' + items.map((it) => `<li>${it}</li>`).join('') + '</ol>');
      continue;
    }

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Paragraph (gather consecutive non-block lines)
    const para = [];
    while (i < lines.length && lines[i].trim() !== '' && !isBlock(lines[i])) { para.push(lines[i]); i++; }
    out.push(`<p class="my-1.5 leading-relaxed">${inline(para.join(' '))}</p>`);
  }

  return DOMPurify.sanitize(out.join(''), { ADD_ATTR: ['target', 'rel'] });
}
